import { type ReactNode, useEffect, useRef, useState } from 'react';
import './JeffPen.css';
import type { PenScene } from './scene/PenScene.ts';
import type { Direction } from '../../../shared/types.ts';
import { JEFF, bowlSideFor } from '../../config/jeff.ts';
import { PEN, type JeffPath, type JeffPose, poseAt } from '../../lib/jeffEngine.ts';

export type PenPhase = 'idle' | 'countdown' | 'walking' | 'result';

interface JeffPenProps {
  phase: PenPhase;
  /** Null until the moment of release. */
  path: JeffPath | null;
  /** Set once the result is locked in, to light the bowl. */
  chosen: Direction | null;
  onArrived: () => void;
  /** Broadcast chrome, countdown and result, layered over the enclosure. */
  children?: ReactNode;
}

/**
 * Hosts the 3D pen.
 *
 * React's entire job here is to create one canvas and hand the scene a pose
 * provider. Every frame after that is the scene's own business — no state, no
 * re-render, no reconciliation during a walk.
 *
 * The provider closes over refs rather than props so the scene never has to be
 * rebuilt: changing the path mid-session would otherwise drop the WebGL
 * context and re-upload every texture.
 */
export function JeffPen({ phase, path, chosen, onArrived, children }: JeffPenProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<PenScene | null>(null);

  // Mutable frame state, read by the pose provider.
  const pathRef = useRef<JeffPath | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const restingRef = useRef<JeffPose>(restingPose(null));
  const arrivedRef = useRef(false);
  const onArrivedRef = useRef(onArrived);
  const phaseRef = useRef(phase);
  const chosenRef = useRef<Direction | null>(null);
  const [ready, setReady] = useState(false);

  // Refreshed after each render rather than during it. The scene's loop fires
  // after paint, so it always sees the callback from the latest commit.
  useEffect(() => {
    onArrivedRef.current = onArrived;
  });

  /**
   * Build the scene once, for the life of the component.
   *
   * three.js is loaded on demand rather than bundled into the entry chunk. It
   * is by far the largest dependency here, and the pages people actually
   * arrive on from a shared link — /call/:id and /record — have no 3D on
   * them at all. Splitting it keeps a Discord click fast and confines the cost
   * to the one screen that earns it.
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let disposed = false;
    let scene: PenScene | null = null;

    void import('./scene/PenScene.ts').then(({ PenScene: Scene }) => {
      // The component can unmount while the chunk is still in flight.
      if (disposed) return;

      scene = new Scene(canvas, JEFF.longBowlSide);
      sceneRef.current = scene;
      setReady(true);

      scene.setPoseProvider((now) => {
        const activePath = pathRef.current;
        const startedAt = startedAtRef.current;

        if (!activePath || startedAt === null) return restingRef.current;

        const elapsed = now - startedAt;
        const pose = poseAt(activePath, elapsed);

        if (elapsed >= activePath.totalMs && !arrivedRef.current) {
          arrivedRef.current = true;
          onArrivedRef.current();
        }
        return pose;
      });

      // Catch up on any state that changed while the chunk was loading.
      scene.setGateOpen(phaseRef.current !== 'idle');
      scene.setChosen(chosenRef.current);
      scene.start();
    });

    return () => {
      disposed = true;
      sceneRef.current = null;
      scene?.dispose();
    };
    // Mount only: the scene is long-lived and reads changing values from refs.
  }, []);

  // Start and stop the walk. `startedAt` is set here rather than in the loop so
  // a slow first frame does not eat into Jeff's deliberation time.
  useEffect(() => {
    if (phase === 'walking' && path) {
      arrivedRef.current = false;
      pathRef.current = path;
      startedAtRef.current = performance.now();
    } else {
      pathRef.current = null;
      startedAtRef.current = null;
      restingRef.current = restingPose(phase === 'result' ? chosen : null);
    }
  }, [phase, path, chosen]);

  useEffect(() => {
    phaseRef.current = phase;
    sceneRef.current?.setGateOpen(phase !== 'idle');
  }, [phase]);

  useEffect(() => {
    const lit = phase === 'result' ? chosen : null;
    chosenRef.current = lit;
    sceneRef.current?.setChosen(lit);
  }, [phase, chosen]);

  return (
    <div className={`pen${ready ? ' pen--ready' : ''}`} data-phase={phase}>
      <canvas className="pen__canvas" ref={canvasRef} aria-hidden="true" />

      {/* The scene is decorative; the state of play is text for everyone else. */}
      <p className="visually-hidden" aria-live="polite">
        {phase === 'idle' && 'Jeff is waiting at the gate.'}
        {phase === 'countdown' && 'Releasing Jeff.'}
        {phase === 'walking' && 'Jeff is deciding.'}
        {phase === 'result' && chosen && `Jeff walked to the ${chosen} bowl.`}
      </p>

      <BowlTag direction="LONG" lit={phase === 'result' && chosen === 'LONG'} />
      <BowlTag direction="SHORT" lit={phase === 'result' && chosen === 'SHORT'} />

      <div className="pen__overlay">{children}</div>
    </div>
  );
}

/**
 * Where Jeff stands when he is not walking: at the gate before a run, and at
 * the chosen bowl after one.
 */
function restingPose(chosen: Direction | null): JeffPose {
  const spot = chosen
    ? { x: PEN.bowls[bowlSideFor(chosen)].x, y: PEN.bowls[bowlSideFor(chosen)].y + PEN.bowlApproachOffsetY }
    : PEN.start;

  return {
    x: spot.x,
    y: spot.y,
    // Heading 0 faces the camera, which from the approach point is also facing
    // the bowl. Correct in both resting states.
    heading: 0,
    behaviour: chosen ? 'arrived' : 'scan',
    progress: chosen ? 1 : 0,
    stride: 0,
    peck: 0,
    moving: false,
    done: chosen !== null,
  };
}

/**
 * Broadcast tag under each bowl. HTML rather than in-scene text: real text
 * stays crisp at any resolution and is readable by a screen reader.
 */
function BowlTag({ direction, lit }: { direction: Direction; lit: boolean }) {
  const side = bowlSideFor(direction);
  return (
    <div
      className={`pen__tag pen__tag--${direction.toLowerCase()} pen__tag--${side}${lit ? ' pen__tag--lit' : ''}`}
    >
      <span className="pen__tag-dot" aria-hidden="true" />
      {direction}
    </div>
  );
}
